import React, { useState } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import Icon from '@components/Icon/Icon';

const TagForm = ({ data, tag, onSubmit, onCancel, isEditing = false, loading: externalLoading = false }) => {
  const { t } = useLanguage();

  // Support both `data` (from common components) and `tag` (legacy)
  const modelData = data || tag;

  const [formData, setFormData] = useState({
    name: modelData?.name || '',
    description: modelData?.description || '',
    color: modelData?.color || '#007bff'
  });
  const [internalLoading, setInternalLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const loading = externalLoading || internalLoading;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('tagsAdmin.validation.nameRequired') || 'Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (externalLoading) {
      // If external loading is managed, just call onSubmit
      try {
        await onSubmit(formData);
      } catch (err) {
        console.error('Failed to submit tag form:', err);
        throw err;
      }
    } else {
      // Manage loading internally
      setInternalLoading(true);
      try {
        await onSubmit(formData);
      } catch (err) {
        console.error('Failed to submit tag form:', err);
        throw err;
      } finally {
        setInternalLoading(false);
      }
    }
  };

  return (
    <form className="sloot-form" onSubmit={handleSubmit}>
      {Object.keys(errors).length > 0 && (
        <div className="error-message">
          {Object.values(errors).join(', ')}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="name">
          {t('tagsAdmin.form.name') || 'Name'} *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={errors.name ? 'error' : ''}
          placeholder={t('tagsAdmin.form.namePlaceholder') || 'Enter tag name'}
          disabled={loading}
          required
        />
        {errors.name && <span className="error-message">{errors.name}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="description">
          {t('tagsAdmin.form.description') || 'Description'}
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder={t('tagsAdmin.form.descriptionPlaceholder') || 'Enter tag description (optional)'}
          disabled={loading}
          rows="3"
        />
      </div>

      <div className="form-group">
        <label htmlFor="color">
          {t('tagsAdmin.form.color') || 'Color'}
        </label>
        <div className="color-input-group">
          <input
            type="color"
            id="color"
            name="color"
            value={formData.color}
            onChange={handleChange}
            disabled={loading}
          />
          <input
            type="text"
            value={formData.color}
            onChange={handleChange}
            name="color"
            placeholder="#007bff"
            disabled={loading}
            className="color-text-input"
          />
        </div>
        <div className="color-preview" style={{ backgroundColor: formData.color }}>
          <span style={{ color: formData.color === '#ffffff' ? '#000000' : '#ffffff' }}>
            {formData.name || t('tagsAdmin.form.preview') || 'Preview'}
          </span>
        </div>
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={loading}
        >
          {t('common.cancel') || 'Cancel'}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading}
        >
          {loading ? (
            <>
              <Icon name="loading" size="sm" />
              {t('common.saving') || 'Saving...'}
            </>
          ) : (
            isEditing ? (t('common.update') || 'Update Tag') : (t('common.create') || 'Create Tag')
          )}
        </button>
      </div>
    </form>
  );
};

export default TagForm;
